import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';

import { useAppDispatch } from 'src/hooks/useStore';
import { getApiErrorMessage } from 'src/services/api';
import {
  getStudioDowngradeBlockers,
  getStudioPlanChangeErrorCode,
  StudioDowngradeBlocker,
  StudioDowngradeSurvey,
  StudioPlanChangeErrorCode,
  StudioPlanChangeRequest,
  useCancelStudioDowngradeMutation,
  useChangeStudioCreditsMutation,
  useDowngradeStudioPlanMutation,
  useGetCurrentPlanQuery,
  useSetupStudioUpgradeCheckoutMutation,
  useUpgradeStudioPlanMutation,
} from 'src/services/api/studioCreditApi';
import { alertSnackbar } from 'src/store/ui/actions';

import { getStudioPlanTitle } from './studioFeatureGating';
import {
  classifyPlanSelection,
  formatStudioPlanDate,
  StudioPlanSelection,
  toApiInterval,
} from './studioPlanFeatures';
import {
  studioUpgradeReturnUrl,
  useStudioUpgradeCheckoutReturn,
} from './useStudioUpgradeCheckoutReturn';

type PlanChangeModal = 'upgrade' | 'survey' | 'blocked';

interface UseStudioPlanChangeOptions {
  /**
   * Fired when an upgrade leaves nothing left to confirm — it applied, or it
   * was already applied. A surface that opened its own modal to reach the
   * confirmation step closes that modal here; the checkout-setup path does not
   * fire it, since the page is navigating to Stripe.
   */
  onUpgradeComplete?: () => void;
}

const toPlanChangeRequest = (
  selection: StudioPlanSelection,
): StudioPlanChangeRequest => ({
  plan: selection.planId,
  interval: toApiInterval(selection.interval),
  credits: selection.tier.credits,
});

export const useStudioPlanChange = ({
  onUpgradeComplete,
}: UseStudioPlanChangeOptions = {}) => {
  const dispatch = useAppDispatch();
  const { data: currentPlan, refetch: refetchCurrentPlan } =
    useGetCurrentPlanQuery();
  // Owned here so every surface with plan-change flows confirms a checkout
  // upgrade that lands back on it.
  useStudioUpgradeCheckoutReturn();

  const [upgradePlan, { isLoading: isUpgrading }] =
    useUpgradeStudioPlanMutation();
  const [setupCheckout, { isLoading: isSettingUpCheckout }] =
    useSetupStudioUpgradeCheckoutMutation();
  const [downgradePlan, { isLoading: isDowngrading }] =
    useDowngradeStudioPlanMutation();
  const [changeCredits, { isLoading: isChangingCredits }] =
    useChangeStudioCreditsMutation();
  const [cancelDowngrade, { isLoading: isCancellingDowngrade }] =
    useCancelStudioDowngradeMutation();

  const [selection, setSelection] = useState<StudioPlanSelection>();
  const [activeModal, setActiveModal] = useState<PlanChangeModal>();
  const [blockers, setBlockers] = useState<StudioDowngradeBlocker[]>([]);

  // Synchronous single-flight latch shared by the three actions: the
  // isLoading-driven disabled states only apply after a rerender, so a rapid
  // double-click needs a check-and-set that happens before any await.
  const inFlightRef = useRef(false);

  const closeModals = useCallback(() => {
    setActiveModal(undefined);
    setSelection(undefined);
  }, []);

  const choosePlan = useCallback(
    (chosen: StudioPlanSelection) => {
      const kind = classifyPlanSelection(chosen, currentPlan);
      // Undefined means there's nothing to send: the current plan hasn't
      // resolved to a Studio subscription (CTAs are disabled then, but a
      // stray click must never reach the backend blind) or the selection
      // matches the current plan exactly.
      if (kind === undefined) {
        return;
      }
      // Grid-initiated downgrades only arise on cross-interval selections
      // (lower ranks are hidden from the grid); the Figma states predate the
      // annual axis, so these route into the standard survey chain — never
      // the retention modal, which is specific to the manage-plan
      // Downgrade-to-Free path.
      setSelection(chosen);
      setActiveModal(kind === 'downgrade' ? 'survey' : 'upgrade');
    },
    [currentPlan],
  );

  // The manage-plan flow's downgrade entry point: opens the reason survey
  // directly for its pre-built one-tier-below target.
  const startDowngrade = useCallback((target: StudioPlanSelection) => {
    setSelection(target);
    setActiveModal('survey');
  }, []);

  // Takes the new TOTAL rather than a plan selection: the credits endpoint
  // needs nothing else, and self-serve Classic has no Studio selection to
  // build.
  const increaseCredits = useCallback(
    async (totalCredits: number) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      try {
        const result = await changeCredits({
          credits: totalCredits,
        }).unwrap();
        dispatch(
          alertSnackbar({
            successMessage: t(
              ($) => $.page_studio_plans.plan_change.credits_increase_success,
              { count: result.credits },
            ),
          }),
        );
      } catch (error) {
        dispatch(
          alertSnackbar({
            errorMessage:
              getApiErrorMessage(error) ??
              t(($) => $.page_studio_plans.plan_change.upgrade_error),
          }),
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [changeCredits, dispatch],
  );

  // A credits-only decrease is give-less, so it schedules at renewal through
  // the downgrade endpoint (no survey — this is not a plan-tier downgrade).
  const decreaseCredits = useCallback(
    async (totalCredits: number) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      try {
        const { pendingChange } = await changeCredits({
          credits: totalCredits,
        }).unwrap();
        dispatch(
          alertSnackbar({
            // A decrease always schedules, so pendingChange is set; the
            // fallback only guards the type when the backend applied the
            // change immediately (a same-total no-op reaching this action).
            successMessage: pendingChange
              ? t(
                  ($) =>
                    $.page_studio_plans.plan_change.credits_decrease_success,
                  {
                    count: pendingChange.credits,
                    date: formatStudioPlanDate(pendingChange.effectiveAt),
                  },
                )
              : t(($) => $.page_studio_plans.plan_change.upgrade_confirming),
          }),
        );
      } catch (error) {
        const blocked = getStudioDowngradeBlockers(error);
        if (blocked) {
          setBlockers(blocked);
          setActiveModal('blocked');
          return;
        }
        dispatch(
          alertSnackbar({
            errorMessage:
              getApiErrorMessage(error) ??
              t(($) => $.page_studio_plans.plan_change.downgrade_error),
          }),
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [changeCredits, dispatch],
  );

  const startCheckout = useCallback(
    async (chosen: StudioPlanSelection) => {
      // Return to this exact page; the marked URL drives the post-checkout
      // confirmation polling.
      const pageUrl = `${window.location.origin}${window.location.pathname}`;
      const request = toPlanChangeRequest(chosen);
      const { url } = await setupCheckout({
        ...request,
        successUrl: studioUpgradeReturnUrl(pageUrl, {
          plan: request.plan,
          credits: request.credits,
        }),
        cancelUrl: pageUrl,
      }).unwrap();
      window.location.assign(url);
    },
    [setupCheckout],
  );

  // totalCredits overrides the card's chosen total with the upgrade modal's
  // additional-credits selection.
  const confirmUpgrade = useCallback(
    async (totalCredits?: number) => {
      if (!selection || inFlightRef.current) {
        return;
      }
      const target: StudioPlanSelection =
        totalCredits !== undefined
          ? { ...selection, tier: { ...selection.tier, credits: totalCredits } }
          : selection;
      inFlightRef.current = true;
      try {
        const result = await upgradePlan(toPlanChangeRequest(target)).unwrap();
        dispatch(
          alertSnackbar({
            successMessage: t(
              ($) => $.page_studio_plans.plan_change.upgrade_success,
              { plan: getStudioPlanTitle(result.plan) },
            ),
          }),
        );
        closeModals();
        onUpgradeComplete?.();
        return;
      } catch (error) {
        const code = getStudioPlanChangeErrorCode(error);
        if (code === StudioPlanChangeErrorCode.paymentMethodRequired) {
          // No card on file (Freemium) — collect one through Stripe checkout;
          // the completion webhook performs the upgrade.
          try {
            await startCheckout(target);
            return;
          } catch (checkoutError) {
            dispatch(
              alertSnackbar({
                errorMessage:
                  getApiErrorMessage(checkoutError) ??
                  t(($) => $.page_studio_plans.plan_change.upgrade_error),
              }),
            );
            return;
          }
        }
        if (code === StudioPlanChangeErrorCode.useDowngradeFlow) {
          setActiveModal('survey');
          return;
        }
        if (code === StudioPlanChangeErrorCode.noChange) {
          dispatch(
            alertSnackbar({
              successMessage: t(
                ($) => $.page_studio_plans.plan_change.no_change,
              ),
            }),
          );
          closeModals();
          onUpgradeComplete?.();
          return;
        }
        dispatch(
          alertSnackbar({
            errorMessage:
              getApiErrorMessage(error) ??
              t(($) => $.page_studio_plans.plan_change.upgrade_error),
          }),
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [
      selection,
      upgradePlan,
      dispatch,
      closeModals,
      startCheckout,
      onUpgradeComplete,
    ],
  );

  const confirmDowngrade = useCallback(
    async (survey: StudioDowngradeSurvey) => {
      if (!selection || inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      try {
        const result = await downgradePlan({
          ...toPlanChangeRequest(selection),
          survey,
        }).unwrap();
        // Going to Free reads as a cancellation (Figma toast copy); a switch
        // to a lower paid plan keeps the plan-specific message.
        dispatch(
          alertSnackbar({
            successMessage:
              selection.planId === 'freemium'
                ? t(
                    ($) =>
                      $.page_studio_plans.plan_change
                        .subscription_cancelled_success,
                  )
                : t(($) => $.page_studio_plans.plan_change.downgrade_success, {
                    plan: getStudioPlanTitle(result.pendingChange.plan),
                    date: formatStudioPlanDate(
                      result.pendingChange.effectiveAt,
                    ),
                  }),
          }),
        );
        closeModals();
      } catch (error) {
        const blocked = getStudioDowngradeBlockers(error);
        if (blocked) {
          setBlockers(blocked);
          setActiveModal('blocked');
          return;
        }
        if (
          getStudioPlanChangeErrorCode(error) ===
          StudioPlanChangeErrorCode.useUpgradeFlow
        ) {
          setActiveModal('upgrade');
          return;
        }
        dispatch(
          alertSnackbar({
            errorMessage:
              getApiErrorMessage(error) ??
              t(($) => $.page_studio_plans.plan_change.downgrade_error),
          }),
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [selection, downgradePlan, dispatch, closeModals],
  );

  const keepCurrentPlan = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    try {
      await cancelDowngrade().unwrap();
      dispatch(
        alertSnackbar({
          successMessage: t(
            ($) => $.page_studio_plans.plan_change.keep_plan_success,
          ),
        }),
      );
    } catch (error) {
      if (
        getStudioPlanChangeErrorCode(error) ===
        StudioPlanChangeErrorCode.noPendingDowngrade
      ) {
        // Already cancelled (e.g. from another tab) — refresh to drop the
        // stale banner.
        void refetchCurrentPlan();
        return;
      }
      dispatch(
        alertSnackbar({
          errorMessage:
            getApiErrorMessage(error) ??
            t(($) => $.page_studio_plans.plan_change.keep_plan_error),
        }),
      );
    } finally {
      inFlightRef.current = false;
    }
  }, [cancelDowngrade, dispatch, refetchCurrentPlan]);

  return {
    currentPlan,
    selection,
    activeModal,
    blockers,
    isUpgrading: isUpgrading || isSettingUpCheckout,
    isDowngrading,
    isChangingCredits,
    isCancellingDowngrade,
    choosePlan,
    startDowngrade,
    increaseCredits,
    decreaseCredits,
    confirmUpgrade,
    confirmDowngrade,
    keepCurrentPlan,
    closeModals,
  };
};
